import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { customerAPI, getApiErrorMessage } from '@matix/api-client';
import type { Customer, AddressInput } from '@matix/types';
import { useLang } from '../../lib/language';
import { useCustomerSearch } from './hooks';
import { AreaPicker } from './AreaPicker';

/**
 * CustomerPicker — search Inv_CashCustomer + inline-create.
 *
 * Two visual states:
 *   - Empty: search input + dropdown of results + "Add new customer" CTA.
 *   - Selected: compact chip with Name · Phone + "Change" link to reopen
 *     the search.
 *
 * Spec section 5.3.
 */
export function CustomerPicker(props: {
  selected: Customer | null;
  onSelect: (c: Customer | null) => void;
}) {
  const { t } = useLang();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  // 300ms debounce on the search input — typing "Ahmed" shouldn't fire 5
  // requests. Empty string → top-N most recent.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  const search = useCustomerSearch(debouncedQ);

  if (props.selected) {
    return (
      <div className="customer-picker selected-state">
        <div className="customer-picker-chip">
          <div className="chip-name">{props.selected.name ?? `#${props.selected.id}`}</div>
          {props.selected.phone && <div className="chip-phone">{props.selected.phone}</div>}
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => props.onSelect(null)}
        >
          {t({ en: 'Change', ar: 'تغيير' })}
        </button>
      </div>
    );
  }

  return (
    <div className="customer-picker empty-state">
      <input
        className="customer-search-input"
        placeholder={t({ en: 'Search by name or phone…', ar: 'ابحث بالاسم أو الهاتف…' })}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      {!addOpen && search.data && (
        <ul className="customer-results">
          {search.data.customers.slice(0, 10).map((c) => (
            <li
              key={c.id}
              className="customer-result"
              onClick={async () => {
                try {
                  const full = await customerAPI.getById(c.id, 'cash');
                  props.onSelect(full);
                } catch {
                  props.onSelect(c);
                }
              }}
            >
              <div className="result-name">{c.name ?? `#${c.id}`}</div>
              <div className="result-meta">
                {c.phone ?? '—'}
              </div>
            </li>
          ))}
          {search.data.customers.length === 0 && (
            <li className="customer-results-empty">{t({ en: 'No customers match.', ar: 'لا يوجد عملاء مطابقون.' })}</li>
          )}
        </ul>
      )}
      {!addOpen ? (
        <button
          type="button"
          className="link-btn add-customer-btn"
          onClick={() => setAddOpen(true)}
        >
          {t({ en: '+ Add new customer', ar: '+ إضافة عميل جديد' })}
        </button>
      ) : (
        <AddCustomerForm
          onCancel={() => setAddOpen(false)}
          onCreated={(c) => {
            props.onSelect(c);
            setAddOpen(false);
          }}
        />
      )}
    </div>
  );
}

interface AddCustomerFormState {
  name: string;
  phone: string;
  name_ar: string;
  email: string;
  withAddress: boolean;
  address: AddressInput;
}

function AddCustomerForm(props: {
  onCancel: () => void;
  onCreated: (c: Customer) => void;
}) {
  const { t } = useLang();
  const [form, setForm] = useState<AddCustomerFormState>({
    name: '',
    phone: '',
    name_ar: '',
    email: '',
    withAddress: false,
    address: {},
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      customerAPI.createCash({
        phone: form.phone.trim(),
        name: form.name.trim() || undefined,
        name_ar: form.name_ar.trim() || null,
        email: form.email.trim() || null,
        ...(form.withAddress && hasAnyAddressField(form.address)
          ? { address: stripEmpty(form.address) }
          : {}),
      }),
    onSuccess: (c) => props.onCreated(c),
    onError: (err) => setError(getApiErrorMessage(err) || t({ en: 'Failed to add customer', ar: 'تعذّر إضافة العميل' })),
  });

  const phoneOk = form.phone.trim().length >= 1;
  // When the cashier opens the address sub-form they MUST fill at least one
  // field — sending an empty `address` is rejected by the backend's refine.
  const addressOk =
    !form.withAddress || hasAnyAddressField(form.address);

  return (
    <form
      className="add-customer-form"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      <div className="form-row">
        <label>{t({ en: 'Name', ar: 'الاسم' })}</label>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={150}
        />
      </div>
      <div className="form-row">
        <label>{t({ en: 'Phone *', ar: 'الهاتف *' })}</label>
        <input
          required
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          maxLength={50}
        />
      </div>
      <div className="form-row">
        <label>{t({ en: 'Name (Arabic)', ar: 'الاسم (بالعربية)' })}</label>
        <input
          dir="rtl"
          value={form.name_ar}
          onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
          maxLength={150}
        />
      </div>
      <div className="form-row">
        <label>{t({ en: 'Email', ar: 'البريد الإلكتروني' })}</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          maxLength={50}
        />
      </div>
      <label className="form-toggle">
        <input
          type="checkbox"
          checked={form.withAddress}
          onChange={(e) => setForm({ ...form, withAddress: e.target.checked })}
        />
        {t({ en: 'Add address now', ar: 'إضافة عنوان الآن' })}
      </label>
      {form.withAddress && (
        <AddressFields
          value={form.address}
          onChange={(address) => setForm({ ...form, address })}
        />
      )}
      {error && <div className="form-error">{error}</div>}
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={props.onCancel}>
          {t({ en: 'Cancel', ar: 'إلغاء' })}
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={!phoneOk || !addressOk || create.isPending}
        >
          {create.isPending ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Add customer', ar: 'إضافة عميل' })}
        </button>
      </div>
    </form>
  );
}

export function AddressFields(props: {
  value: AddressInput;
  onChange: (next: AddressInput) => void;
}) {
  const { t } = useLang();
  const { value, onChange } = props;
  function patch<K extends keyof AddressInput>(k: K, v: AddressInput[K]) {
    onChange({ ...value, [k]: v });
  }
  return (
    <div className="address-fields">
      <div className="form-row form-row-area">
        <label>{t({ en: 'Area', ar: 'المنطقة' })}</label>
        <AreaPicker
          value={value.area_id ?? null}
          onChange={(id) => patch('area_id', id)}
        />
      </div>

      <div className="address-grid">
        <div className="form-cell">
          <label>{t({ en: 'Avenue', ar: 'الجادة' })}</label>
          <input value={value.avenue ?? ''} onChange={(e) => patch('avenue', e.target.value)} maxLength={150} />
        </div>
        <div className="form-cell">
          <label>{t({ en: 'Block', ar: 'القطعة' })}</label>
          <input value={value.block ?? ''} onChange={(e) => patch('block', e.target.value)} maxLength={150} />
        </div>
        <div className="form-cell">
          <label>{t({ en: 'Street', ar: 'الشارع' })}</label>
          <input value={value.street ?? ''} onChange={(e) => patch('street', e.target.value)} maxLength={150} />
        </div>
        <div className="form-cell">
          <label>{t({ en: 'House', ar: 'المنزل' })}</label>
          <input value={value.house ?? ''} onChange={(e) => patch('house', e.target.value)} maxLength={150} />
        </div>
        <div className="form-cell">
          <label>{t({ en: 'Floor', ar: 'الطابق' })}</label>
          <input value={value.floor ?? ''} onChange={(e) => patch('floor', e.target.value)} maxLength={150} />
        </div>
        <div className="form-cell">
          <label>{t({ en: 'Flat', ar: 'الشقة' })}</label>
          <input value={value.flat ?? ''} onChange={(e) => patch('flat', e.target.value)} maxLength={150} />
        </div>
      </div>

      <div className="form-row form-row-note">
        <label>{t({ en: 'Address note', ar: 'ملاحظة العنوان' })}</label>
        <textarea
          value={value.address_note ?? ''}
          onChange={(e) => patch('address_note', e.target.value)}
          rows={3}
          placeholder={t({ en: 'Landmarks, gate code, building name…', ar: 'معالم قريبة، رمز البوابة، اسم المبنى…' })}
        />
      </div>
    </div>
  );
}

export function hasAnyAddressField(a: AddressInput): boolean {
  return Boolean(
    a.area_id ||
      (a.street && a.street.trim()) ||
      (a.house && a.house.trim()) ||
      (a.address_note && a.address_note.trim()),
  );
}

export function stripEmpty(a: AddressInput): AddressInput {
  const out: AddressInput = {};
  for (const k of Object.keys(a) as Array<keyof AddressInput>) {
    const v = a[k];
    if (v == null) continue;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) (out as Record<string, unknown>)[k] = t;
    } else {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}
